import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "../components/AddNotePad.css";

const API_BASE = "https://newerp.awasthivitality.com/api";

/* ===============================
   AUTH + TOKEN HELPERS
================================ */
const getAuthHeaders = () => {
  const token =
    localStorage.getItem("session_token") ||
    localStorage.getItem("token") ||
    "";

  return {
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };
};

// ✅ ADDED (IMPORTANT)
const appendToken = (url) => {
  const token =
    localStorage.getItem("session_token") ||
    localStorage.getItem("token");

  if (!token) return url;

  try {
    const u = new URL(url);
    if (!u.searchParams.has("token")) {
      u.searchParams.append("token", token);
    }
    return u.toString();
  } catch {
    return url;
  }
};

/* ===============================
   COMPONENT
================================ */
const AddNotePad = () => {
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [loadingPatients, setLoadingPatients] = useState(false);

  const [note, setNote] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // success | error
  const [saving, setSaving] = useState(false);

  const searchRef = useRef(null);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);

  const navigate = useNavigate();

  /* ===============================
     FETCH PATIENTS
  ================================ */
  useEffect(() => {
    fetchPatients();
  }, []);

  const fetchPatients = async () => {
    try {
      setLoadingPatients(true);

      const res = await axios.get(
        appendToken(`${API_BASE}/patients/search`),
        { headers: getAuthHeaders() }
      );

      const arr = Array.isArray(res.data?.data) ? res.data.data : [];
      setPatients(arr);
      setFilteredPatients(arr);
    } catch (err) {
      console.error("Fetch patients error:", err);
      setPatients([]);
      setFilteredPatients([]);
    } finally {
      setLoadingPatients(false);
    }
  };

  /* ===============================
     SEARCH PATIENTS
  ================================ */
  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      searchPatients(searchTerm);
    }, 300);

    return () => clearTimeout(timeoutRef.current);
  }, [searchTerm]);

  const searchPatients = async (query) => {
    try {
      setLoadingPatients(true);

      const url = query.trim()
        ? `${API_BASE}/patients/search?q=${encodeURIComponent(query)}`
        : `${API_BASE}/patients/search`;

      const res = await axios.get(appendToken(url), {
        headers: getAuthHeaders(),
      });

      setFilteredPatients(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      console.error("Search error:", err);
      setFilteredPatients([]);
    } finally {
      setLoadingPatients(false);
    }
  };

  /* ===============================
     CLOSE DROPDOWN
  ================================ */
  useEffect(() => {
    const handler = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        searchRef.current &&
        !searchRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* ===============================
     SELECT PATIENT
  ================================ */
  const handlePatientSelect = (p) => {
    setSelectedPatient(p);
    setSearchTerm(p.name);
    setShowDropdown(false);
  };

  const clearPatient = () => {
    setSelectedPatient(null);
    setSearchTerm("");
    setFilteredPatients(patients);
    setShowDropdown(true);
  };

  /* ===============================
     SAVE NOTE
  ================================ */
  const handleSave = async () => {
    if (!selectedPatient) {
      setMessage("Please select a patient.");
      setMessageType("error");
      return;
    }

    if (!note.trim()) {
      setMessage("Please enter a note.");
      setMessageType("error");
      return;
    }

    setSaving(true);
    setMessage("");
    setMessageType("");

    try {
      // ✅ TOKEN APPENDED IN URL (MAIN FIX)
      const res = await axios.post(
        appendToken(
          `${API_BASE}/patient/notepad/add/${selectedPatient.patient_id}`
        ),
        { pdn_message: note.trim() },
        {
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
        }
      );

      if (res.data?.success) {
        setMessage("Note added successfully!");
        setMessageType("success");
        setNote("");

        setTimeout(() => navigate("/PatientNotepad"), 800);
      } else {
        setMessage(res.data?.msg || "Something went wrong.");
        setMessageType("error");
      }
    } catch (err) {
      console.error("Save note error:", err);
      setMessage("Server error. Please try again.");
      setMessageType("error");
    } finally {
      setSaving(false);
    }
  };

  /* ===============================
     UI
  ================================ */
  return (
    <div className="addnote-wrapper">
      <h2 className="addnote-title">Add Patient Note</h2>

      <div className="addnote-select-group">
        <label className="addnote-label">Search Patient</label>

        <div className="addnote-search-box" ref={searchRef}>
          <input
            type="text"
            className="addnote-search-input"
            placeholder="Search by name / PR / mobile"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
          />

          {selectedPatient && (
            <button className="addnote-clear-btn" onClick={clearPatient}>
              ✕
            </button>
          )}

          {showDropdown && (
            <div className="addnote-dropdown" ref={dropdownRef}>
              {loadingPatients ? (
                <div className="addnote-no-results">Loading...</div>
              ) : filteredPatients.length > 0 ? (
                filteredPatients.map((p) => (
                  <div
                    key={p.patient_id}
                    className="addnote-dropdown-item"
                    onClick={() => handlePatientSelect(p)}
                  >
                    <div className="addnote-item-name">{p.name}</div>
                    <div className="addnote-item-sub">
                      PR: {p.pr_no} | Mobile: {p.mobile}
                    </div>
                    <div className="addnote-item-id">
                      ID: {p.patient_id}
                    </div>
                  </div>
                ))
              ) : (
                <div className="addnote-no-results">No patients found</div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="addnote-form">
        <label className="addnote-label">Note Message</label>
        <textarea
          className="addnote-textarea"
          rows="6"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Write patient note..."
        />

        <div className="addnote-actions">
          <button
            className="addnote-savebtn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Note"}
          </button>

          <button className="addnote-clearbtn" onClick={() => setNote("")}>
            Clear
          </button>
        </div>

        {message && (
          <div className={`addnote-message addnote-${messageType}`}>
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddNotePad;